/**
 * K2.5 (docs/LOOP-KI-AGENT.md) — das Warte-Erlebnis der Komposition IM CHAT.
 *
 * ── Das Problem ──────────────────────────────────────────────────────────────────────────────
 * „Komponiere ein Angebot für diese Anfrage" laeuft im Chat als EIN Werkzeugaufruf mit
 * `timeoutMs: 170_000` (apps/agent/src/maestro-agent.ts, `angebot_komponieren`). Gemessene
 * Laufzeiten: **70–91 Sekunden** (LOOP-KI-AGENT K0.7 Runde 3 — `dauerMs 70137` — und K1.5).
 * Sichtbar war davon eine Zeile: „MAESTRO arbeitet …". Ueber anderthalb Minuten ist das nicht
 * von „haengt" zu unterscheiden.
 *
 * ── Die Quelle: dieselbe wie beim Knopf, kein zweiter Fortschrittsweg ────────────────────────
 * Die Komponier-Route schreibt ihren Stand seit dem 05.08. wieder nach `inquiries.generation_phase`
 * (`setzePhase` in apps/api/src/routes/inquiries.ts): `thinking` vor dem Modellaufruf, `writing`
 * danach, im `finally` zurueck auf `null`. Genau dieses Feld liest das Fortschritts-Popup der
 * Anfragenseite — und genau dieses Feld liest jetzt auch der Chat, ueber den schmalen Lesepunkt
 * `GET /api/inquiries/laufende-komposition`.
 *
 * ── Was hier NICHT passiert ──────────────────────────────────────────────────────────────────
 * Kein Prozentwert, kein Fortschrittsbalken, keine Restzeit-Prognose. Der Server meldet drei
 * Zustaende, also zeigt die Karte drei Schritte. Die einzige Zahl ist die tatsaechlich
 * verstrichene Wartezeit — gemessen, nicht geschaetzt — daneben die belegte Spanne aus den
 * Messungen oben. Das ist die Hausregel: beschreibend und belegt, nie geraten.
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "./components";
import { apiFetch } from "../lib/api";

/** Die beiden Phasen, die die Komponier-Route wirklich schreibt (sql/150, `setzePhase`). */
export type KompositionsPhase = "thinking" | "writing";

/** Eine laufende Komposition, wie sie `GET /api/inquiries/laufende-komposition` meldet. */
export interface LaufendeKomposition {
  inquiryId: string;
  kunde: string;
  gaeste: number | null;
  phase: KompositionsPhase;
}

export interface Fortschrittsschritt {
  text: string;
  /** `fertig` = nachweislich vorbei, `laeuft` = genau jetzt, `offen` = steht noch aus. */
  zustand: "fertig" | "laeuft" | "offen";
}

/**
 * Die drei Schritte — jeder benennt, was der Server an dieser Stelle TATSAECHLICH getan hat:
 *  1. vor `setzePhase('thinking')` sind Anfrage, Katalog, Bedingungen und vergleichbare Faelle
 *     geladen (die grosse `withTenant`-Transaktion der Route),
 *  2. `thinking` = der eine Modellaufruf laeuft,
 *  3. `writing`  = Varianten sind da, Positionen/Anschreiben werden geschrieben.
 * Eine vierte Zeile gaebe es nur, wenn der Server einen vierten Stand melden wuerde.
 */
export function baueKompositionsSchritte(lauf: LaufendeKomposition): Fortschrittsschritt[] {
  const schreibt = lauf.phase === "writing";
  return [
    { text: "Anfrage, Katalog und frühere Fälle gelesen", zustand: "fertig" },
    { text: "MAESTRO stellt die Varianten zusammen", zustand: schreibt ? "fertig" : "laeuft" },
    { text: "Positionen und Anschreiben werden gespeichert", zustand: schreibt ? "laeuft" : "offen" },
  ];
}

/** m:ss — dieselbe Lesart wie eine Stoppuhr; Sekunden abgerundet, nie aufgerundet geschoent. */
export function formatiereWartezeit(ms: number): string {
  const gesamt = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(gesamt / 60)}:${String(gesamt % 60).padStart(2, "0")}`;
}

/**
 * Abstand der Abfragen. KEIN neuer Wert: exakt der, mit dem das Fortschritts-Popup der
 * Anfragenseite dasselbe Feld liest (`apps/web/src/pages/inquiries.tsx`, `setInterval(… , 700)`).
 * Zwei verschiedene Takte fuer dieselbe Quelle waeren zwei Verhalten fuer eine Sache.
 */
const ABFRAGE_MS = 700;

/**
 * Fragt den echten Stand ab, solange `aktiv` ist (im Chat: solange ein Turn laeuft).
 *
 * ── Nur der EIGENE Lauf ──────────────────────────────────────────────────────────────────────
 * Die Route meldet jede laufende Komposition des Betriebs — auch die einer Kollegin am
 * Nachbarplatz oder die des Knopfes „KI-Angebot erzeugen" in einem zweiten Tab. Ueber DIE zu
 * schreiben „Ihr Angebot entsteht" waere eine Behauptung, die dieser Chat nicht belegen kann.
 *
 * Deshalb die erste erfolgreiche Antwort als GRUNDLINIE: was da schon laeuft, lief bereits, als
 * dieser Turn begann — der Chat wartet nicht darauf. Angezeigt wird nur, was danach neu
 * dazukommt. Das ist zulaessig, weil der Werkzeugaufruf erst Sekunden nach dem Turn-Start
 * beginnt (das Modell muss ihn erst waehlen) und die Route ihre Phase noch spaeter setzt.
 * Faellt die erste Abfrage aus, bleibt die Karte im Zweifel weg — lieber der schlichte Spinner
 * als ein fremder Vorgang unter falschem Namen.
 *
 * Fehler bleiben still: Fortschritt ist Komfort. Eine fehlgeschlagene Abfrage darf im Chat
 * niemals eine Fehlermeldung erzeugen — der eigentliche Vorgang laeuft davon unbeeindruckt
 * weiter (dieselbe Haltung wie `setzePhase` serverseitig).
 */
export function useLaufendeKomposition(aktiv: boolean): LaufendeKomposition | null {
  const [lauf, setLauf] = useState<LaufendeKomposition | null>(null);
  const laeuftRef = useRef(false);
  /** Ids, die schon liefen, als dieser Turn begann. `null` = Grundlinie steht noch aus. */
  const grundlinieRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!aktiv) {
      setLauf(null);
      grundlinieRef.current = null;
      return;
    }
    let lebt = true;
    grundlinieRef.current = null;
    const hole = async () => {
      // Keine ueberlappenden Abfragen: eine langsame Antwort darf sich nicht stapeln.
      if (laeuftRef.current) return;
      laeuftRef.current = true;
      try {
        const res = await apiFetch("/api/inquiries/laufende-komposition");
        if (!res.ok) return;
        const body = (await res.json()) as { data?: LaufendeKomposition[] };
        if (!lebt) return;
        const laufende = body.data ?? [];
        if (grundlinieRef.current === null) {
          grundlinieRef.current = new Set(laufende.map((l) => l.inquiryId));
          return; // Erste Antwort ist die Grundlinie, keine Anzeige.
        }
        // Mehrere neue gleichzeitig? Dann die aelteste (die Route sortiert nach Eingang) — die
        // Karte zeigt EINEN Vorgang, und der erste ist der, auf den gewartet wird.
        setLauf(laufende.find((l) => !grundlinieRef.current!.has(l.inquiryId)) ?? null);
      } catch {
        /* still - siehe Kommentar oben */
      } finally {
        laeuftRef.current = false;
      }
    };
    void hole();
    const iv = setInterval(() => void hole(), ABFRAGE_MS);
    return () => {
      lebt = false;
      clearInterval(iv);
    };
  }, [aktiv]);

  return lauf;
}

/**
 * Die Karte im Chat-Verlauf. Sie ersetzt fuer die Dauer der Komposition die Zeile
 * „MAESTRO arbeitet …" — dieselbe Stelle, mehr Inhalt.
 *
 * `aria-live="polite"`: derselbe Grund wie bei den Schrittzeilen darueber (P5) — ein
 * Fortschritt, den ein Screenreader nicht vorliest, existiert fuer einen Teil der Nutzer nicht.
 */
export function KompositionsFortschritt({ lauf, wartetSeitMs }: { lauf: LaufendeKomposition; wartetSeitMs: number }) {
  const schritte = baueKompositionsSchritte(lauf);
  const kopf = [lauf.kunde, lauf.gaeste != null ? `${lauf.gaeste} Gäste` : null].filter(Boolean).join(" · ");
  return (
    <div
      aria-live="polite"
      aria-label="Fortschritt der Angebots-Komposition"
      className="rounded-2xl border border-outline-variant/40 bg-surface-container-low px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.03]"
    >
      <div className="flex items-center gap-2">
        <Icon name="auto_awesome" className="text-[18px] text-primary" />
        <span className="text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Ihr Angebot entsteht</span>
      </div>
      {kopf && <p className="mt-0.5 text-[12px] text-on-surface-variant dark:text-surface-variant">{kopf}</p>}

      <ul className="mt-2.5 flex flex-col gap-1.5">
        {schritte.map((s) => (
          <li key={s.text} className="flex items-start gap-2 text-[12px] leading-relaxed">
            <span aria-hidden="true" className="mt-[1px]">
              {s.zustand === "fertig" ? (
                <Icon name="check" className="text-[14px] text-secondary dark:text-[#b8ae9e]" />
              ) : s.zustand === "laeuft" ? (
                <Icon name="progress_activity" className="animate-spin text-[14px] text-primary" />
              ) : (
                <span className="block h-[13px] w-[13px] rounded-full border border-dashed border-outline-variant" />
              )}
            </span>
            <span className="sr-only">{s.zustand === "fertig" ? "Erledigt:" : s.zustand === "laeuft" ? "Läuft gerade:" : "Steht aus:"}</span>
            <span
              className={
                s.zustand === "laeuft"
                  ? "font-semibold text-primary"
                  : s.zustand === "fertig"
                    ? "text-on-surface-variant dark:text-inverse-on-surface/80"
                    : "text-on-surface-variant/60 dark:text-surface-variant/60"
              }
            >
              {s.text}
            </span>
          </li>
        ))}
      </ul>

      {/* Die einzige Zahl der Karte ist gemessen: die verstrichene Wartezeit. Daneben die
          Spanne aus den echten Laeufen (LOOP-KI-AGENT K0.7/K1.5) — als Einordnung, nicht als
          Prognose fuer genau diesen Lauf. */}
      <p className="mt-2.5 text-[11px] text-on-surface-variant dark:text-surface-variant">
        Wartezeit {formatiereWartezeit(wartetSeitMs)} · bisher gemessen: 70–91 Sekunden je Komposition
      </p>
    </div>
  );
}
